import { isNil } from 'lodash';

import { jsonParse, jsonStringify } from 'utils/json';
import { logger } from 'utils/logger';

import {
  getAllSubCompaniesCommandCenterPageConfigUrl,
  getCommandCenterPageConfigUrl,
  getSubCompanyCommandCenterPageConfigUrl,
} from './commandCenterPageConfigApi.constants';

const NO_CONTENT_STATUS = 204;

// A broker (sub-company user) lacks read access to the company-level / sub-company
// config and gets a 403. The page already falls back to the in-code default layout
// (+ any sub-company override), so skip the forbidden interceptor: without it the
// shared 403 handler fires window.location.reload(), churning the broker /home.
const SKIP_FORBIDDEN_CONFIG = { skipResponseInterceptor: { forbiddenError: true } };

/**
 * Real client for the per-company configurable Command Center homepage (DEV-3636,
 * Skynet.Accounts behind the admin/skynet gateway). The layout blob
 * ({ default:{widgets}, roleOverrides }) is persisted verbatim as an opaque
 * JSON string the backend never inspects, so the client serializes on write and
 * parses on read.
 *
 * Get resolves `{ data: null }` when the backend has nothing stored (204), so
 * the page falls back to its in-code DEFAULT_COMMAND_CENTER_CONFIG. Reset (DELETE)
 * clears the stored row and likewise resolves null.
 */
export class CommandCenterPageConfigApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  // The stored blob is opaque user-authored content the backend never validates,
  // so a malformed string (truncated write, hand-edited row) would make a raw
  // JSON.parse throw and break the page on every load. Fail loud-but-recoverable:
  // treat an unparseable blob as absent (null) so the page falls back to the
  // inherited/default config instead of surfacing a SyntaxError.
  #parseConfigOrNull(raw) {
    try {
      return jsonParse(raw);
    } catch (error) {
      logger.error(error);

      return null;
    }
  }

  async getCommandCenterPageConfig({ companyId }) {
    const response = await this.#http.get(
      getCommandCenterPageConfigUrl(companyId),
      SKIP_FORBIDDEN_CONFIG,
    );

    if (response.status === NO_CONTENT_STATUS || isNil(response.data?.config)) {
      return { data: null };
    }

    return { data: this.#parseConfigOrNull(response.data.config) };
  }

  async updateCommandCenterPageConfig({ companyId, config }) {
    const { data } = await this.#http.put(getCommandCenterPageConfigUrl(companyId), {
      config: jsonStringify(config),
    });

    return {
      data: isNil(data?.config) ? config : (this.#parseConfigOrNull(data.config) ?? config),
    };
  }

  async resetCommandCenterPageConfig({ companyId }) {
    await this.#http.delete(getCommandCenterPageConfigUrl(companyId));

    return { data: null };
  }

  // DEV-4136: per-subcompany config override. The backend endpoint ships in a
  // separate PR; callers must handle 404 gracefully until it is deployed.
  async getSubCompanyCommandCenterPageConfig({ companyId, subCompanyId }) {
    const response = await this.#http.get(
      getSubCompanyCommandCenterPageConfigUrl(companyId, subCompanyId),
      SKIP_FORBIDDEN_CONFIG,
    );

    if (response.status === NO_CONTENT_STATUS || isNil(response.data?.config)) {
      return { data: null };
    }

    return { data: this.#parseConfigOrNull(response.data.config) };
  }

  async updateSubCompanyCommandCenterPageConfig({ companyId, subCompanyId, config }) {
    const { data } = await this.#http.put(
      getSubCompanyCommandCenterPageConfigUrl(companyId, subCompanyId),
      { config: jsonStringify(config) },
    );

    return {
      data: isNil(data?.config) ? config : (this.#parseConfigOrNull(data.config) ?? config),
    };
  }

  async resetSubCompanyCommandCenterPageConfig({ companyId, subCompanyId }) {
    await this.#http.delete(getSubCompanyCommandCenterPageConfigUrl(companyId, subCompanyId));

    return { data: null };
  }

  // DEV-4136: the "All subcompanies (default)" scope — one config every subcompany
  // inherits unless it has its own per-subcompany override. The backend endpoint
  // ships separately (ConfigScope=AllSubCompanies); callers must handle 404
  // gracefully until it is deployed (pending-banner pattern).
  async getAllSubCompaniesCommandCenterPageConfig({ companyId }) {
    const response = await this.#http.get(getAllSubCompaniesCommandCenterPageConfigUrl(companyId));

    if (response.status === NO_CONTENT_STATUS || isNil(response.data?.config)) {
      return { data: null };
    }

    return { data: this.#parseConfigOrNull(response.data.config) };
  }

  async updateAllSubCompaniesCommandCenterPageConfig({ companyId, config }) {
    const { data } = await this.#http.put(getAllSubCompaniesCommandCenterPageConfigUrl(companyId), {
      config: jsonStringify(config),
    });

    return {
      data: isNil(data?.config) ? config : (this.#parseConfigOrNull(data.config) ?? config),
    };
  }

  async resetAllSubCompaniesCommandCenterPageConfig({ companyId }) {
    await this.#http.delete(getAllSubCompaniesCommandCenterPageConfigUrl(companyId));

    return { data: null };
  }
}
